/**
 * Platform Analytics visual theme. Centralizes the Cedar palette for places
 * where Tailwind classes don't reach — chart color arrays, visx scales, SVG
 * gradients, etc.
 *
 * Source of truth: specs/DESIGN_TOKENS.md (Cedar Brand v2.0, March 2026).
 * For Tailwind-based styling, prefer the `coach-cedar-*` classes wired in
 * tailwind.config.js.
 */

export const CEDAR = {
  prime:  '#1F6C4F',  // 60% — primary
  dark:   '#113C2B',  // depth
  stream: '#42A5A5',  // teal accent
  light:  '#5FA287',  // hover / success
  subtle: '#E8F2ED',  // soft backgrounds
  mist:   '#A8D1BF',  // mid-tone
  ink:    '#0B1614',  // body text
  limestone: '#F3F3F3',
} as const;

export const SEMANTIC = {
  success: '#378F8F',
  warning: '#F59E0B',
  error:   '#C75D5D',
  info:    '#42A5A5',
  neutral: '#9CA3AF',
} as const;

/**
 * Categorical palette for distinct series (channels, tiers, engines, etc).
 * 6 cedar-family colors tuned for side-by-side legibility, plus neutrals.
 * Don't exceed 6 series on a single chart — beyond that, use a heatmap.
 */
export const CATEGORICAL: readonly string[] = [
  CEDAR.prime,     // deep green
  CEDAR.stream,    // teal
  CEDAR.mist,      // pale green
  '#8B6FBB',       // muted violet
  '#E8A948',       // warm amber
  '#C75D5D',       // coral
] as const;

/**
 * Sequential ramp for heatmaps (cohort retention, memory recalls).
 * 9-stop GnBu-style gradient rooted in Cedar Prime. For visx's
 * `scaleSequential(interpolator)` path or manual D3 scale.
 */
export const SEQUENTIAL: readonly string[] = [
  '#F3F9F5',
  '#E4F0EA',
  '#C8E1D4',
  '#A4CDB6',
  '#78B490',
  '#4E9970',
  '#2D7F55',
  '#1A6340',
  '#113C2B',
] as const;

/**
 * Diverging palette for delta-style visuals (above/below baseline).
 * Cool (below) → neutral → warm (above).
 */
export const DIVERGING: readonly string[] = [
  '#4E9970',
  '#78B490',
  '#C8E1D4',
  '#F3F3F3',
  '#EFC29F',
  '#E8A948',
  '#C75D5D',
] as const;

/**
 * Tier-specific colors — keep these stable across tabs so users develop
 * muscle memory. Names match the internal DB/code names.
 */
export const TIER_COLORS: Record<string, string> = {
  trial:    '#A8D1BF',
  basic:    '#5FA287',
  full:     '#1F6C4F',
  extended: '#113C2B',
  beta:     '#B08968',
  system:   '#6B7280',
  unknown:  '#9CA3AF',
};

export const TIER_DISPLAY: Record<string, string> = {
  trial:    'Trial',
  basic:    'Essentials',
  full:     'Chat+',
  extended: 'Pro',
  beta:     'Beta testers',
  system:   'System (NK-CAT, pipelines)',
  unknown:  'Unknown',
};

/**
 * Relationship tier colors (Power / Steady / At-risk / Dormant).
 */
export const RELATIONSHIP_TIER_COLORS: Record<string, string> = {
  power:   '#1F6C4F',
  steady:  '#5FA287',
  at_risk: '#E8A948',
  dormant: '#9CA3AF',
};

export const RELATIONSHIP_TIER_LABEL: Record<string, string> = {
  power:   'Power',
  steady:  'Steady',
  at_risk: 'At risk',
  dormant: 'Dormant',
};
